import { DateTime } from 'luxon';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from 'react';
import {
  Alert,
  Button,
  Form,
  InputGroup,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import api, {
  type InventoryItem,
  type ShopItem,
  type User,
} from '../../service/api';

interface ManagerUserInventoryModalProps {
  show: boolean;
  onHide: () => void;
  user: User | null;
}

export default function ManagerUserInventoryModal({
  show,
  onHide,
  user,
}: ManagerUserInventoryModalProps) {
  const { t } = useTranslation();

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [shopItems, setShopItems] = useState<ShopItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [isShopLoading, setIsShopLoading] = useState(false);
  const [mainError, setMainError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [addAmount, setAddAmount] = useState(1);
  const [editedAmounts, setEditedAmounts] = useState<Record<string, string>>(
    {},
  );
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const resetState = useCallback(() => {
    setInventory([]);
    setShopItems([]);
    setIsLoading(false);
    setIsActionInProgress(false);
    setIsShopLoading(false);
    setMainError(null);
    setAddError(null);
    setSelectedItemId('');
    setAddAmount(1);
    setEditedAmounts({});
    setShowHistoryModal(false);
    setShowAddModal(false);
  }, []);

  const syncEditedAmounts = useCallback((items: InventoryItem[]) => {
    setEditedAmounts(() => {
      const updated: Record<string, string> = {};
      items.forEach(item => {
        if (item.status === 'available') {
          updated[item._id] = item.amount.toString();
        }
      });
      return updated;
    });
  }, []);

  const loadInitialData = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setMainError(null);
    try {
      const inventoryResponse = await api.inventoryManagerGetUserInventory(
        user._id,
      );
      setInventory(inventoryResponse);
      syncEditedAmounts(inventoryResponse);
    } catch (err) {
      setMainError(
        err instanceof Error
          ? err.message
          : t('manager.users.inventory.genericError'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [syncEditedAmounts, t, user]);

  const refreshInventory = useCallback(async () => {
    if (!user) return;
    setIsActionInProgress(true);
    setMainError(null);
    try {
      const inventoryResponse = await api.inventoryManagerGetUserInventory(
        user._id,
      );
      setInventory(inventoryResponse);
      syncEditedAmounts(inventoryResponse);
    } catch (err) {
      setMainError(
        err instanceof Error
          ? err.message
          : t('manager.users.inventory.genericError'),
      );
    } finally {
      setIsActionInProgress(false);
    }
  }, [syncEditedAmounts, t, user]);

  const loadShopItems = useCallback(async () => {
    setIsShopLoading(true);
    setAddError(null);
    try {
      const items = await api.shopItemsGet();
      setShopItems(items);
    } catch (err) {
      setAddError(
        err instanceof Error
          ? err.message
          : t('manager.users.inventory.genericError'),
      );
    } finally {
      setIsShopLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (show && user) {
      void loadInitialData();
    }
  }, [show, user, loadInitialData]);

  useEffect(() => {
    if (!show) {
      resetState();
    }
  }, [resetState, show]);

  useEffect(() => {
    if (showAddModal && user) {
      setAddError(null);
      if (shopItems.length === 0) {
        void loadShopItems();
      }
    }
  }, [loadShopItems, shopItems.length, showAddModal, user]);

  const handleAmountChange = (inventoryItemId: string, value: string) => {
    setEditedAmounts(current => ({ ...current, [inventoryItemId]: value }));
  };

  const handleSaveAmount = async (inventoryItemId: string) => {
    if (!user) return;
    const enteredValue = editedAmounts[inventoryItemId];
    const parsedAmount = Number(enteredValue);

    if (!Number.isInteger(parsedAmount) || parsedAmount <= 0) {
      setMainError(t('manager.users.inventory.invalidAmount'));
      return;
    }

    setIsActionInProgress(true);
    setMainError(null);

    try {
      await api.inventoryManagerUpdateItem(user._id, inventoryItemId, {
        amount: parsedAmount,
      });
      await refreshInventory();
    } catch (err) {
      setMainError(
        err instanceof Error
          ? err.message
          : t('manager.users.inventory.genericError'),
      );
      setIsActionInProgress(false);
    }
  };

  const handleRemoveItem = async (inventoryItemId: string) => {
    if (!user) return;

    setIsActionInProgress(true);
    setMainError(null);

    try {
      await api.inventoryManagerRemoveItem(user._id, inventoryItemId);
      await refreshInventory();
    } catch (err) {
      setMainError(
        err instanceof Error
          ? err.message
          : t('manager.users.inventory.genericError'),
      );
      setIsActionInProgress(false);
    }
  };

  const handleAddInventoryItem = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) return;

    const amountValue = Number(addAmount);
    if (!selectedItemId) {
      setAddError(t('manager.users.inventory.selectItemError'));
      return;
    }
    if (!Number.isInteger(amountValue) || amountValue <= 0) {
      setAddError(t('manager.users.inventory.invalidAmount'));
      return;
    }

    setIsActionInProgress(true);
    setAddError(null);

    try {
      await api.inventoryManagerAddItem(user._id, {
        itemId: selectedItemId,
        amount: amountValue,
      });
      setSelectedItemId('');
      setAddAmount(1);
      await refreshInventory();
      setShowAddModal(false);
    } catch (err) {
      setAddError(
        err instanceof Error
          ? err.message
          : t('manager.users.inventory.genericError'),
      );
      setIsActionInProgress(false);
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddError(null);
    setSelectedItemId('');
    setAddAmount(1);
  };

  const availableItems = useMemo(
    () => inventory.filter(item => item.status === 'available'),
    [inventory],
  );

  const historyItems = useMemo(
    () => inventory.filter(item => item.status !== 'available'),
    [inventory],
  );

  const availableShopItems = useMemo(
    () => [...shopItems].sort((a, b) => a.title.localeCompare(b.title)),
    [shopItems],
  );

  const modalTitle = useMemo(() => {
    if (!user) {
      return t('manager.users.inventory.title');
    }
    return t('manager.users.inventory.titleForUser', {
      name: `${user.given_name} ${user.family_name}`,
    });
  }, [t, user]);

  const formatDate = (value?: string) => {
    if (!value) return '';
    const date = DateTime.fromISO(value);
    return date.isValid ? date.toLocaleString(DateTime.DATETIME_MED) : value;
  };

  const isMainLoading = isLoading || (!user && show);

  return (
    <>
      <Modal show={show} onHide={onHide} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!user && (
            <p className="text-muted mb-0">
              {t('manager.users.inventory.noUserSelected')}
            </p>
          )}

          {user && (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between flex-wrap gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowHistoryModal(true)}
                  disabled={isMainLoading}
                >
                  {t('manager.users.inventory.viewHistory')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowAddModal(true);
                    setAddError(null);
                    setSelectedItemId('');
                    setAddAmount(1);
                  }}
                  disabled={isActionInProgress || isMainLoading}
                >
                  {t('manager.users.inventory.openAddModal')}
                </Button>
              </div>

              {mainError && (
                <Alert variant="danger" className="mb-0">
                  {mainError}
                </Alert>
              )}

              {isMainLoading && (
                <div className="text-center py-3">
                  <Spinner animation="border" role="status" />
                </div>
              )}

              {!isMainLoading && availableItems.length === 0 && (
                <p className="text-muted text-center mb-0">
                  {t('manager.users.inventory.empty')}
                </p>
              )}

              {!isMainLoading &&
                availableItems.map(item => {
                  const currentValue =
                    editedAmounts[item._id] ?? item.amount.toString();
                  const parsedValue = Number(currentValue);
                  const isValidAmount =
                    Number.isInteger(parsedValue) && parsedValue > 0;
                  const isChanged = parsedValue !== item.amount;

                  return (
                    <div
                      key={item._id}
                      className="border rounded p-3 shadow-sm"
                    >
                      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                        <div>
                          <div className="fw-semibold">{item.item.title}</div>
                          <div className="text-muted small">
                            {item.item.description}
                          </div>
                        </div>
                        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 w-100">
                          <InputGroup
                            size="sm"
                            className="flex-grow-1"
                          >
                            <InputGroup.Text>
                              {t('manager.users.inventory.amountShort')}
                            </InputGroup.Text>
                            <Form.Control
                              type="number"
                              min={1}
                              value={currentValue}
                              onChange={event =>
                                handleAmountChange(item._id, event.target.value)
                              }
                              disabled={isActionInProgress}
                            />
                          </InputGroup>
                          <div className="d-flex gap-2 ms-sm-auto">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleSaveAmount(item._id)}
                              disabled={
                                isActionInProgress ||
                                !isValidAmount ||
                                !isChanged
                              }
                            >
                              {t('common.save')}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleRemoveItem(item._id)}
                              disabled={isActionInProgress}
                            >
                              {t('common.remove')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t('common.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('manager.users.inventory.historyTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyItems.length === 0 ? (
            <p className="text-muted mb-0">
              {t('manager.users.inventory.historyEmpty')}
            </p>
          ) : (
            <div className="d-flex flex-column gap-3">
              {historyItems.map(item => (
                <div key={item._id} className="border rounded p-3 shadow-sm">
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div className="fw-semibold">{item.item.title}</div>
                    </div>
                    <div className="text-end">
                      {item.status === 'activated' && (
                        <div className="text-muted small">
                          {formatDate(item.processedAt)}
                        </div>
                      )}
                      {item.status === 'pending_activation' && (
                        <div className="text-muted small">
                          {formatDate(item.activationRequestedAt)}
                        </div>
                      )}
                      {item.processedBy && (
                        <div className="text-muted small">
                          {t('manager.users.inventory.processedBy', {
                            name: `${item.processedBy.given_name} ${item.processedBy.family_name}`,
                          })}
                        </div>
                      )}
                      <div className="text-muted small">
                        {t('manager.users.inventory.amountLabel')}:{' '}
                        {item.amount}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowHistoryModal(false)}
          >
            {t('common.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('manager.users.inventory.addModalTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addError && <Alert variant="danger">{addError}</Alert>}

          <Form
            id="manager-user-inventory-add-form"
            onSubmit={handleAddInventoryItem}
          >
            <Form.Group
              controlId="manager-user-inventory-item"
              className="mb-3"
            >
              <Form.Label>{t('manager.users.inventory.itemLabel')}</Form.Label>
              <Form.Select
                value={selectedItemId}
                onChange={event => setSelectedItemId(event.target.value)}
                disabled={isActionInProgress || isShopLoading}
              >
                <option value="">
                  {t('manager.users.inventory.selectItemPlaceholder')}
                </option>
                {availableShopItems.map(item => (
                  <option key={item._id} value={item._id}>
                    {item.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group
              controlId="manager-user-inventory-amount"
              className="mb-3"
            >
              <Form.Label>
                {t('manager.users.inventory.amountLabel')}
              </Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={addAmount}
                onChange={event => setAddAmount(Number(event.target.value))}
                disabled={isActionInProgress}
              />
            </Form.Group>
          </Form>

          {isShopLoading && (
            <div className="text-muted small">
              {t('manager.users.inventory.shopLoading')}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseAddModal}
            disabled={isActionInProgress}
          >
            {t('common.close')}
          </Button>
          <Button
            form="manager-user-inventory-add-form"
            type="submit"
            variant="primary"
            disabled={isActionInProgress || isShopLoading}
          >
            {isActionInProgress
              ? t('common.saving')
              : t('manager.users.inventory.addButton')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
